import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getChatRooms, getChatStaff, getOrCreateDM,
  getChatMessages, sendChatMessage, sendChatFile, markChatRoomRead,
  searchChatRefs, uploadsUrl,
} from '../api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateDdMmmYy } from '../utils/format';
import { isUserActive } from '../utils/activity';

// ── Types ──────────────────────────────────────────────────────────────────

interface ChatRoom {
  id: number; type: 'DM' | 'GROUP'; name: string; unread: number;
  otherStaff: { id: number; staffName: string } | null;
  lastMessage: { content: string | null; fileName: string | null; senderName: string; createdAt: string } | null;
}

interface ChatFile {
  id: number; fileName: string; originalName: string; mimeType: string; fileSize: number; expiresAt: string;
}

interface Message {
  id: number; roomId: number; content: string | null; createdAt: string;
  sender: { id: number; staffName: string }; files: ChatFile[];
}

interface StaffMember { id: number; staffName: string; }

interface RefResult { type: 'task' | 'subtask'; id: number; label: string; code: string; parentTask: string | null; }

// ── Token helpers ──────────────────────────────────────────────────────────
// Tokens stored in message content: [task:123:Task Name] [subtask:456:Sub Name]

const TOKEN_RE = /\[(task|subtask):(\d+):([^\]]+)\]/g;

function insertToken(text: string, cursorPos: number, trigger: string, token: string): [string, number] {
  // Find the @ trigger before the cursor
  const before = text.slice(0, cursorPos);
  const triggerIdx = before.lastIndexOf('@');
  if (triggerIdx === -1) return [text + token, text.length + token.length];
  const newText = text.slice(0, triggerIdx) + token + ' ' + text.slice(cursorPos);
  const newCursor = triggerIdx + token.length + 1;
  return [newText, newCursor];
}

// ── Render helpers ─────────────────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const today = new Date(); const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return formatDateDdMmmYy(d);
}

function fileIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType.includes('word')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  return '📎';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const API_BASE = process.env.REACT_APP_API_URL ?? '';

// ── MessageContent — renders text with [task:…] chips ─────────────────────

const MessageContent: React.FC<{ content: string; isMe: boolean }> = ({ content, isMe }) => {
  const navigate = useNavigate();
  const parts: React.ReactNode[] = [];
  let last = 0;
  const re = new RegExp(TOKEN_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) parts.push(<span key={last}>{content.slice(last, m.index)}</span>);
    const [, type, id, label] = m;
    const to = type === 'task' ? `/tasks` : `/tasks`;
    parts.push(
      <button
        key={m.index}
        onClick={() => navigate(to)}
        title={type === 'task' ? `Task #${id}` : `Subtask #${id}`}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold mx-0.5 transition-colors ${
          isMe
            ? 'bg-white/25 text-white hover:bg-white/40'
            : type === 'task'
            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
        }`}
      >
        {type === 'task' ? '📋' : '✅'} {label}
      </button>
    );
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push(<span key={last}>{content.slice(last)}</span>);
  return <>{parts}</>;
};

// ── Chat page ──────────────────────────────────────────────────────────────

const Chat: React.FC = () => {
  const { user } = useAuth();
  const myStaffId = user?.staffId;

  const [rooms, setRooms]               = useState<ChatRoom[]>([]);
  const [activeRoomId, setActiveRoomId] = useState<number | null>(null);
  const [messages, setMessages]         = useState<Message[]>([]);
  const [text, setText]                 = useState('');
  const [sending, setSending]           = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMore, setHasMore]           = useState(false);

  // New DM
  const [showNewDM, setShowNewDM]   = useState(false);
  const [allStaff, setAllStaff]     = useState<StaffMember[]>([]);
  const [dmSearch, setDmSearch]     = useState('');

  // @ mention
  const [mentionQuery, setMentionQuery]   = useState('');
  const [mentionResults, setMentionResults] = useState<RefResult[]>([]);
  const [mentionOpen, setMentionOpen]     = useState(false);
  const [mentionIdx, setMentionIdx]       = useState(0);
  const mentionDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const messagesEndRef  = useRef<HTMLDivElement>(null);
  const fileInputRef    = useRef<HTMLInputElement>(null);
  const textareaRef     = useRef<HTMLTextAreaElement>(null);
  const pollRef         = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastMsgIdRef    = useRef<number>(0);

  const activeRoom = rooms.find((r) => r.id === activeRoomId) ?? null;

  // ── Visibility-aware polling guard ────────────────────────────────────────

  // Poll only when the user is actually looking at the app — visible tab AND
  // focused window. Walking away to another app/window pauses all polling.
  const isVisible = useCallback(() => isUserActive(), []);

  // ── Room list ─────────────────────────────────────────────────────────────

  const fetchRooms = useCallback(async () => {
    if (!isVisible()) return;
    try {
      const res = await getChatRooms();
      setRooms(res.data);
    } catch { /* ignore */ }
  }, [isVisible]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  useEffect(() => {
    const id = setInterval(fetchRooms, 10000);
    const onVisible = () => { if (isVisible()) fetchRooms(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [fetchRooms, isVisible]);

  // ── Messages ──────────────────────────────────────────────────────────────

  const loadMessages = useCallback(async (roomId: number) => {
    setLoadingMessages(true);
    try {
      const res = await getChatMessages(roomId);
      const data: Message[] = res.data;
      setMessages(data);
      setHasMore(data.length >= 50);
      lastMsgIdRef.current = data[data.length - 1]?.id ?? 0;
    } finally { setLoadingMessages(false); }
  }, []);

  useEffect(() => {
    if (!activeRoomId) return;
    loadMessages(activeRoomId);
    markChatRoomRead(activeRoomId).catch(() => {});
    setRooms((prev) => prev.map((r) => r.id === activeRoomId ? { ...r, unread: 0 } : r));
  }, [activeRoomId, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 2s poll — only when tab is visible
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!activeRoomId) return;
    pollRef.current = setInterval(async () => {
      if (!isVisible() || !activeRoomId) return;
      const after = lastMsgIdRef.current;
      try {
        const res = await getChatMessages(activeRoomId, { after });
        const newMsgs: Message[] = res.data;
        if (newMsgs.length > 0) {
          setMessages((prev) => [...prev, ...newMsgs]);
          lastMsgIdRef.current = newMsgs[newMsgs.length - 1].id;
          markChatRoomRead(activeRoomId).catch(() => {});
          setRooms((prev) => prev.map((r) => r.id === activeRoomId ? { ...r, unread: 0 } : r));
        }
      } catch { /* ignore */ }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeRoomId, isVisible]);

  // ── Load older messages ───────────────────────────────────────────────────

  const loadOlder = async () => {
    if (!activeRoomId || messages.length === 0) return;
    const before = messages[0].id;
    try {
      const res = await getChatMessages(activeRoomId, { before });
      const older: Message[] = res.data;
      setMessages((prev) => [...older, ...prev]);
      setHasMore(older.length >= 50);
    } catch { /* ignore */ }
  };

  // ── Send ──────────────────────────────────────────────────────────────────

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeRoomId || !text.trim() || sending) return;
    setSending(true);
    try {
      const res = await sendChatMessage(activeRoomId, text.trim());
      setMessages((prev) => [...prev, res.data]);
      lastMsgIdRef.current = res.data.id;
      setText('');
      fetchRooms();
    } finally { setSending(false); }
  };

  // ── @ mention handling ────────────────────────────────────────────────────

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setText(val);

    const cursor = e.target.selectionStart ?? val.length;
    const before = val.slice(0, cursor);
    const atIdx = before.lastIndexOf('@');

    if (atIdx !== -1 && !before.slice(atIdx).includes(' ')) {
      const query = before.slice(atIdx + 1);
      setMentionQuery(query);
      setMentionOpen(true);
      setMentionIdx(0);
      if (mentionDebounce.current) clearTimeout(mentionDebounce.current);
      mentionDebounce.current = setTimeout(async () => {
        if (query.length >= 2) {
          try {
            const res = await searchChatRefs(query);
            setMentionResults(res.data);
          } catch { setMentionResults([]); }
        } else {
          setMentionResults([]);
        }
      }, 250);
    } else {
      setMentionOpen(false);
      setMentionResults([]);
    }
  };

  const selectMention = (ref: RefResult) => {
    const cursor = textareaRef.current?.selectionStart ?? text.length;
    const token = `[${ref.type}:${ref.id}:${ref.label}]`;
    const [newText, newCursor] = insertToken(text, cursor, mentionQuery, token);
    setText(newText);
    setMentionOpen(false);
    setMentionResults([]);
    // Restore focus + cursor
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newCursor, newCursor);
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionOpen && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => (i + 1) % mentionResults.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIdx((i) => (i - 1 + mentionResults.length) % mentionResults.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectMention(mentionResults[mentionIdx]); return; }
      if (e.key === 'Escape') { setMentionOpen(false); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e as any); }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeRoomId) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await sendChatFile(activeRoomId, fd);
      setMessages((prev) => [...prev, res.data]);
      lastMsgIdRef.current = res.data.id;
      fetchRooms();
    } catch (err: any) {
      alert(err?.response?.data?.message ?? 'Upload failed');
    }
    e.target.value = '';
  };

  // ── New DM ────────────────────────────────────────────────────────────────

  const openNewDM = async () => {
    if (!allStaff.length) {
      const res = await getChatStaff();
      setAllStaff(res.data);
    }
    setShowNewDM(true);
    setDmSearch('');
  };

  const startDM = async (targetId: number) => {
    setShowNewDM(false);
    try {
      const res = await getOrCreateDM(targetId);
      await fetchRooms();
      setActiveRoomId(res.data.id);
    } catch { /* ignore */ }
  };

  const filteredStaff = allStaff.filter((s) =>
    s.staffName.toLowerCase().includes(dmSearch.toLowerCase())
  );

  // ── Day separators ────────────────────────────────────────────────────────

  const messagesWithDividers = useMemo(() => {
    const result: Array<{ type: 'divider'; label: string } | { type: 'message'; msg: Message }> = [];
    let lastDate = '';
    for (const msg of messages) {
      const d = formatDate(msg.createdAt);
      if (d !== lastDate) { result.push({ type: 'divider', label: d }); lastDate = d; }
      result.push({ type: 'message', msg });
    }
    return result;
  }, [messages]);

  // ── Render ────────────────────────────────────────────────────────────────

  const groupRooms = rooms.filter((r) => r.type === 'GROUP');
  const dmRooms    = rooms.filter((r) => r.type === 'DM');

  return (
    <div className="flex h-full -m-6 overflow-hidden rounded-lg border border-gray-200 bg-white">

      {/* ── Left panel: room list ── */}
      <aside className="w-60 shrink-0 border-r border-gray-200 flex flex-col bg-gray-50">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <span className="font-semibold text-gray-800 text-sm">Team Chat</span>
          <button onClick={openNewDM} className="text-blue-600 hover:text-blue-800 text-xs font-medium transition-colors" title="New Direct Message">
            + DM
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          <div className="px-3 pb-1">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold px-1 mb-1">Channels</p>
            {groupRooms.map((r) => (
              <RoomItem key={r.id} room={r} active={r.id === activeRoomId} onClick={() => setActiveRoomId(r.id)} />
            ))}
          </div>
          {dmRooms.length > 0 && (
            <div className="px-3 mt-2">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold px-1 mb-1">Direct Messages</p>
              {dmRooms.map((r) => (
                <RoomItem key={r.id} room={r} active={r.id === activeRoomId} onClick={() => setActiveRoomId(r.id)} />
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* ── Right panel ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {activeRoom ? (
          <>
            {/* Header */}
            <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-3 shrink-0">
              <span className="text-base">{activeRoom.type === 'GROUP' ? '#' : '👤'}</span>
              <div>
                <p className="text-sm font-semibold text-gray-900">{activeRoom.name}</p>
                {activeRoom.type === 'GROUP' && <p className="text-xs text-gray-400">Group channel</p>}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1">
              {hasMore && (
                <div className="text-center mb-2">
                  <button onClick={loadOlder} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                    Load older messages
                  </button>
                </div>
              )}
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                </div>
              ) : (
                messagesWithDividers.map((item, i) =>
                  item.type === 'divider' ? (
                    <div key={`d-${i}`} className="flex items-center gap-3 py-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-[11px] text-gray-400 font-medium">{item.label}</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                  ) : (
                    <MessageBubble key={item.msg.id} msg={item.msg} isMe={item.msg.sender.id === myStaffId} apiBase={API_BASE} />
                  )
                )
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 border-t border-gray-200 shrink-0">
              {/* @ mention dropdown */}
              {mentionOpen && mentionResults.length > 0 && (
                <div className="mb-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-52 overflow-y-auto">
                  <div className="px-3 py-1.5 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-[11px] text-gray-400 font-medium">Link task or subtask</span>
                    <span className="text-[10px] text-gray-300">↑↓ navigate · Enter select · Esc dismiss</span>
                  </div>
                  {mentionResults.map((ref, i) => (
                    <button
                      key={`${ref.type}-${ref.id}`}
                      onMouseDown={(e) => { e.preventDefault(); selectMention(ref); }}
                      className={`w-full text-left flex items-center gap-2 px-3 py-2 transition-colors ${i === mentionIdx ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    >
                      <span className="text-sm shrink-0">{ref.type === 'task' ? '📋' : '✅'}</span>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-800 truncate">{ref.label}</p>
                        <p className="text-[10px] text-gray-400">
                          {ref.type === 'task' ? `Task · ${ref.code}` : `Subtask · ${ref.code}${ref.parentTask ? ` · ${ref.parentTask}` : ''}`}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <form onSubmit={handleSend} className="flex items-end gap-2">
                <button type="button" onClick={() => fileInputRef.current?.click()} className="shrink-0 p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors" title="Attach file">
                  📎
                </button>
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" />
                <div className="flex-1 relative">
                  <textarea
                    ref={textareaRef}
                    value={text}
                    onChange={handleTextChange}
                    onKeyDown={handleKeyDown}
                    placeholder={`Message ${activeRoom.name}… (type @ to tag a task)`}
                    rows={1}
                    className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 max-h-32"
                    style={{ overflowY: text.split('\n').length > 3 ? 'auto' : 'hidden' }}
                  />
                </div>
                <button type="submit" disabled={!text.trim() || sending} className="shrink-0 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors">
                  Send
                </button>
              </form>
              <p className="text-[11px] text-gray-400 mt-1 pl-10">Enter to send · Shift+Enter for newline · @ to tag a task</p>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-3">
            <span className="text-5xl">💬</span>
            <p className="text-sm font-medium text-gray-500">Select a channel or start a DM</p>
            <button onClick={openNewDM} className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
              + New Direct Message
            </button>
          </div>
        )}
      </div>

      {/* ── New DM modal ── */}
      {showNewDM && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-80 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-800">New Direct Message</span>
              <button onClick={() => setShowNewDM(false)} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
            </div>
            <div className="px-4 py-2">
              <input autoFocus className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="Search staff…" value={dmSearch} onChange={(e) => setDmSearch(e.target.value)} />
            </div>
            <div className="overflow-y-auto max-h-60 px-2 pb-2">
              {filteredStaff.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-6">No staff found</p>
              ) : (
                filteredStaff.map((s) => (
                  <button key={s.id} onClick={() => startDM(s.id)} className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors">
                    <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0">
                      {s.staffName.charAt(0).toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-800">{s.staffName}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── RoomItem ────────────────────────────────────────────────────────────────

const RoomItem: React.FC<{ room: ChatRoom; active: boolean; onClick: () => void }> = ({ room, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors mb-0.5 ${active ? 'bg-blue-100 text-blue-800' : 'text-gray-700 hover:bg-gray-100'}`}
  >
    {room.type === 'GROUP' ? (
      <span className="text-sm text-gray-500 shrink-0 w-5 text-center">#</span>
    ) : (
      <span className="w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold flex items-center justify-center shrink-0">
        {room.name.charAt(0).toUpperCase()}
      </span>
    )}
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between">
        <span className={`text-xs font-medium truncate ${active ? 'text-blue-800' : 'text-gray-800'}`}>{room.name}</span>
        {room.lastMessage && <span className="text-[10px] text-gray-400 shrink-0 ml-1">{relativeTime(room.lastMessage.createdAt)}</span>}
      </div>
      {room.lastMessage && (
        <p className="text-[10px] text-gray-400 truncate">{room.lastMessage.content?.replace(TOKEN_RE, (_, type, _id, label) => `[${label}]`) ?? room.lastMessage.fileName ?? ''}</p>
      )}
    </div>
    {room.unread > 0 && (
      <span className="shrink-0 min-w-[18px] h-[18px] rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
        {room.unread > 99 ? '99+' : room.unread}
      </span>
    )}
  </button>
);

// ── MessageBubble ───────────────────────────────────────────────────────────

const MessageBubble: React.FC<{ msg: Message; isMe: boolean; apiBase: string }> = ({ msg, isMe, apiBase }) => {
  const isImage = (mime: string) => mime.startsWith('image/');
  return (
    <div className={`flex gap-2 group ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5 ${isMe ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
        {msg.sender.staffName.charAt(0).toUpperCase()}
      </div>
      <div className={`max-w-[70%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
        <div className={`flex items-baseline gap-1.5 mb-0.5 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
          <span className="text-[11px] font-semibold text-gray-700">{msg.sender.staffName}</span>
          <span className="text-[10px] text-gray-400">{formatTime(msg.createdAt)}</span>
        </div>
        {msg.content && (
          <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${isMe ? 'bg-blue-500 text-white rounded-tr-sm' : 'bg-gray-100 text-gray-800 rounded-tl-sm'}`}>
            <MessageContent content={msg.content} isMe={isMe} />
          </div>
        )}
        {msg.files.map((f) => (
          <div key={f.id} className="mt-1">
            {isImage(f.mimeType) ? (
              <a href={uploadsUrl(`chat/${f.fileName}`)} target="_blank" rel="noopener noreferrer">
                <img src={uploadsUrl(`chat/${f.fileName}`)} alt={f.originalName} className="max-w-xs max-h-48 rounded-xl border border-gray-200 object-cover" />
              </a>
            ) : (
              <a
                href={uploadsUrl(`chat/${f.fileName}`)} target="_blank" rel="noopener noreferrer"
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${isMe ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                <span className="text-base">{fileIcon(f.mimeType)}</span>
                <div className="min-w-0">
                  <p className="truncate max-w-[200px]">{f.originalName}</p>
                  <p className="text-[11px] text-gray-400">{formatBytes(f.fileSize)}</p>
                </div>
                <span className="ml-1 text-gray-400">↓</span>
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Chat;
